import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import Link from 'next/link';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import getPlatform from '../../../common/utils/getPlatform';
import AppDownloadModal2 from '../../AppDownloadModal2';
import Image from 'next/image';

export default function RegionSection() {
  const [isAppDownloadModalVisible, setIsAppDownloadModalVisible] = useState(false);

  return (
    <Container>
      <Wrapper>
        <Top>
          <TitleWrapper>
            <FakeTitle>전국에서 만나는 우리집 청소</FakeTitle>
            <TitleDesktop>{TitleDesktopSvg}</TitleDesktop>
            <TitleTablet>{TitleTabletSvg}</TitleTablet>
          </TitleWrapper>
          <TopContent>
            <Description>
              서비스는 전국 대부분 지역에서 이용 가능하며, 주소를 입력하면 정확한 서비스 가능 지역을 확인할 수 있습니다.
            </Description>
            <Link href="https://res.cleaninglab.co.kr/u/web_to_app.html" passHref>
              <Button
                onClick={(e) => {
                  // 모바일이면 href로 이동, PC면 다운로드 팝업 띄우기
                  const platform = getPlatform();

                  if (platform === 'mobile' || platform === 'tablet') return;

                  e.preventDefault();
                  setIsAppDownloadModalVisible(true);
                }}
              >
                앱에서 확인
              </Button>
            </Link>
          </TopContent>
        </Top>
        <Bottom>
          <RegionDl>
            <RegionDt>수도권</RegionDt>
            <RegionDd>서울특별시, 인천광역시, 경기도</RegionDd>
            <RegionDt>경상권</RegionDt>
            <RegionDd>부산광역시, 대구광역시, 울산광역시, 경산시, 김해시, 양산시, 창원시, 포항시, 경주시</RegionDd>
            <RegionDt>충청권</RegionDt>
            <RegionDd>대전광역시, 천안시, 아산시, 청주시, 세종시</RegionDd>
            <RegionDt>전라권</RegionDt>
            <RegionDd>광주광역시, 전주시, 완주군, 순천시, 광양시, 여수시</RegionDd>
            <RegionDt>강원권</RegionDt>
            <RegionDd>춘천시, 원주시</RegionDd>
          </RegionDl>
          <Map
            initial={{ y: 48, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <Image
              src={'https://cleanhome-web.cleaninglab.co.kr/brandsite/home_clean/HomeCleaning_E_ImgA_250224.png'}
              alt={''}
              width={502}
              height={452}
              layout={'responsive'}
            />
          </Map>
        </Bottom>
      </Wrapper>
      <AppDownloadModal2
        visible={isAppDownloadModalVisible}
        setVisible={setIsAppDownloadModalVisible}
        hasBodyScrollLock
      />
    </Container>
  );
}

const Container = styled.section`
  position: relative;
  width: 100%;
  background: #f5f6f8;
  overflow: hidden;
  padding: 0 24px;
`;

const Wrapper = styled.div`
  margin: 0 auto;
  max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.desktop};

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.tablet};
  }
`;

const Top = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  padding-top: 100px;
  padding-bottom: 120px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding-top: 76px;
    padding-bottom: 60px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    flex-direction: column;
    padding-top: 86px;
    padding-bottom: 40px;
  }
`;

const TitleWrapper = styled.div`
  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    margin-bottom: 24px;
  }
`;

const FakeTitle = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  margin: -1px;
  clip: rect(0, 0, 0, 0);
  text-indent: -5000px;
`;

const TitleDesktop = styled.div`
  display: block;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: none;
  }
`;

const TitleTablet = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: block;
  }
`;

const TopContent = styled.div``;

const Description = styled.h2`
  word-break: keep-all;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 300;
  font-size: 21px;
  line-height: 140%;
  margin-bottom: 24px;
  width: 502px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    font-size: 17px;
    margin-bottom: 26px;
    width: 405px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    margin-bottom: 44px;
    width: auto;
    font-weight: 400;
  }
`;

const Button = styled.a`
  display: inline-block;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  border: 1px solid #999999;
  border-radius: 8px;
  padding: 12px 20px;
  transition: 0.2s;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding: 10px 16px;
    font-size: 15px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    padding: 9px 16px;
  }

  &:hover {
    color: #ffffff;
    background: #42484c;
    border-color: #42484c;
  }
`;

const Bottom = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-bottom: 80px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding-bottom: 80px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    flex-direction: column-reverse;
    padding-bottom: 60px;
  }
`;

const RegionDl = styled.dl`
  flex: 0 0 40%;
`;

const RegionDt = styled.dt`
  display: inline-block;
  font-weight: 600;
  font-size: 19px;
  line-height: 140%;
  color: #00bed6;
  margin-bottom: 7px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    margin-bottom: 6px;
    font-size: 14px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    font-size: 15px;
  }
`;

const RegionDd = styled.dd`
  font-weight: 300;
  font-size: 24px;
  line-height: 140%;
  color: rgba(0, 0, 0, 0.85);
  margin-bottom: 38px;
  word-break: keep-all;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    margin-bottom: 24px;
    font-size: 19px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    margin-bottom: 30px;
    font-weight: 400;
  }
`;

const Map = styled(motion.div)`
  position: relative;
  width: 502px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    /* width: 405px; */
    width: 312px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    width: 100%;
    margin-bottom: 50px;
  }
`;

const MapImg = styled.img`
  width: 100%;
`;

const Br = styled.br`
  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    display: none;
  }
`;

const TitleDesktopSvg = (
  <svg width="300" height="105" viewBox="0 0 300 105" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M37.87 42.934H12.386V27.892H17.492V38.518H37.87V42.934ZM37.088 30.836H31.982V15.978H23.472V11.608H31.982V0.705998H37.088V30.836ZM11.052 7.79H2.358V3.466H24.76V7.79H16.388V8.986C16.388 15.104 20.206 19.566 26.646 21.406L24.254 25.5C19.93 24.396 15.56 20.9 13.858 16.898C12.202 21.084 8.154 24.396 3.508 26.19L1.07 22.05C7.188 19.842 11.052 15.288 11.052 9.078V7.79ZM74.7124 44.268H69.6064V33.182H48.0324V28.812H59.0264V21.774H42.1904V17.404H68.5484C68.6864 16.898 68.7784 16.346 68.8704 15.794C69.1004 14.736 69.2384 13.724 69.3764 12.804C69.6064 10.964 69.7444 8.342 69.6984 6.502H48.2164V2.086H74.7584C74.8964 5.72 74.8044 8.94 74.4364 11.746C74.2064 13.724 73.8844 15.61 73.5164 17.404H80.9224V21.774H64.0864V28.812H74.7124V44.268ZM88.5548 15.196C88.5548 19.474 90.9008 21.958 93.8908 21.958C97.0188 21.958 99.2728 19.474 99.2728 15.196C99.2728 10.964 97.0188 8.388 93.8908 8.388C90.9008 8.388 88.5548 10.964 88.5548 15.196ZM115.373 39.116V0.751998H120.387V39.116H115.373ZM83.6788 15.242C83.6788 8.388 87.9568 3.88 93.8908 3.88C98.4908 3.88 101.895 6.41 103.459 10.596C103.689 11.286 103.873 11.976 104.011 12.712H107.185V1.304H112.061V37.644H107.185V17.082H104.149C104.011 18.094 103.781 19.06 103.459 19.934C101.895 24.028 98.5368 26.604 93.8908 26.604C87.9568 26.604 83.6788 22.096 83.6788 15.242ZM150.375 25.04L147.155 28.904C143.291 26.466 139.427 22.28 137.955 18.094C136.483 22.51 132.987 26.788 128.387 29.778L125.075 26.006C131.147 22.326 135.149 16.254 135.149 7.928V2.362H140.669V7.698C140.669 16.024 144.763 21.544 150.375 25.04ZM161.323 39.07H156.217V16.116H147.753V11.516H156.217V0.751998H161.323V39.07ZM212.898 42.934H187.322V27.8H192.474V38.518H212.898V42.934ZM194.682 18.922V7.33H184.378V18.922H194.682ZM199.696 23.292H179.226V2.96H199.696V23.292ZM218.648 15.886H211.656V30.928H206.55V0.705998H211.656V11.516H218.648V15.886ZM244.543 21.59L245.003 25.73C238.563 27.064 231.019 27.524 221.313 27.524V3.006H226.465V23.2C233.043 23.338 239.023 22.648 244.543 21.59ZM260.045 17.634H253.053V39.07H247.901V0.751998H253.053V13.264H260.045V17.634ZM299.877 25.546H261.145V21.222H299.877V25.546ZM267.677 1.12H272.829V12.206H293.989V16.622H267.677V1.12ZM267.447 29.134H272.507V38.702H293.943V43.026H267.447V29.134ZM27.428 71.516C27.428 68.02 24.346 65.536 20.16 65.536C15.836 65.536 12.892 68.02 12.892 71.516C12.892 74.92 15.836 77.634 20.16 77.634C24.346 77.634 27.428 74.92 27.428 71.516ZM22.69 102.474H17.676V88.674H0.794V84.35H39.526V88.674H22.69V102.474ZM32.672 71.516C32.672 78.324 26.554 82.004 20.16 82.004C13.674 82.004 7.648 78.324 7.648 71.516C7.648 65.03 13.352 60.982 20.16 60.982C27.152 60.982 32.672 65.076 32.672 71.516ZM69.0544 83.43L69.6064 87.708C63.4424 89.134 52.4944 89.41 44.8584 89.042V73.402H59.1644V67.284H44.7664V62.868H64.2704V77.726H49.9644V84.764C54.8404 84.994 63.7644 84.534 69.0544 83.43ZM72.9184 99.07V60.752H77.9784V99.07H72.9184ZM114.775 99.852V94.976H100.055V99.852H114.775ZM114.775 83.66V60.66H119.881V83.66H114.775ZM94.7188 66.916H86.0248V62.546H108.381V66.916H100.055C100.101 69.906 101.067 72.436 102.861 74.414C104.747 76.438 107.277 77.956 110.451 78.968L107.967 83.108C103.505 81.636 99.4568 78.554 97.4328 74.552C95.7768 78.646 91.6828 81.958 87.0828 83.798L84.6908 79.612C87.7728 78.508 90.2108 76.898 92.0048 74.736C93.7988 72.62 94.6728 69.998 94.7188 66.916ZM119.881 104.222H94.9488V85.96H100.055V90.698H114.775V85.96H119.881V104.222ZM156.46 65.214H143.304V60.844H156.46V65.214ZM167.546 95.804C167.546 92.86 164.97 91.02 161.152 91.02C157.472 91.02 154.85 92.86 154.85 95.804C154.85 98.748 157.472 100.588 161.152 100.588C164.97 100.588 167.546 98.748 167.546 95.804ZM173.25 86.144H168.144V78.14H160.876V73.862H168.144V60.614H173.25V86.144ZM147.122 72.206H138.52V67.882H161.198V72.206H152.872C152.964 73.172 153.194 74.092 153.516 74.874C154.896 78.14 158.3 80.302 162.394 81.406L160.186 85.546C156.138 84.304 151.492 81.314 150.02 78.048C148.594 81.36 144.27 85.04 139.716 86.374L137.324 82.326C141.28 80.808 144.822 78.692 146.386 75.15C146.8 74.276 147.03 73.31 147.122 72.206ZM172.698 95.804C172.698 101.186 167.914 104.958 161.152 104.958C154.528 104.958 149.698 101.186 149.698 95.804C149.698 90.514 154.528 86.696 161.152 86.696C167.914 86.696 172.698 90.514 172.698 95.804ZM217.084 94.976H178.352V90.56H195.234V81.636H200.248V90.56H217.084V94.976ZM194.774 61.81H200.662V64.34C200.662 71.746 207.056 76.76 214.232 78.738L211.886 83.154C205.124 81.13 200.11 77.22 197.718 72.252C195.556 77.266 190.036 81.084 183.596 83.2L181.204 78.784C188.472 76.944 194.774 71.7 194.774 64.34V61.81Z"
      fill="black"
      fill-opacity="0.85"
    />
  </svg>
);

const TitleTabletSvg = (
  <svg width="180" height="65" viewBox="0 0 180 65" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M21.815 25.483H6.857V16.654H9.854V22.891H21.815V25.483ZM21.356 18.382H18.359V9.661H13.364V7.096H18.359V0.696999H21.356V18.382ZM6.074 4.855H0.971V2.317H14.12V4.855H9.206V5.557C9.206 9.148 11.447 11.767 15.227 12.847L13.823 15.25C11.285 14.602 8.72 12.55 7.721 10.201C6.749 12.658 4.373 14.602 1.646 15.655L0.215 13.225C3.806 11.929 6.074 9.256 6.074 5.611V4.855ZM43.9799 26.266H40.9829V19.759H28.3199V17.194H34.7729V13.063H24.8909V10.498H40.3619C40.4429 10.201 40.4969 9.877 40.5509 9.553C40.6859 8.932 40.7669 8.338 40.8479 7.798C40.9829 6.718 41.0639 5.179 41.0369 4.099H28.4279V1.507H44.0069C44.0879 3.64 44.0339 5.53 43.8179 7.177C43.6829 8.338 43.4939 9.445 43.2779 10.498H47.6249V13.063H37.7429V17.194H43.9799V26.266ZM52.6448 9.202C52.6448 11.713 54.0218 13.171 55.7768 13.171C57.6128 13.171 58.9358 11.713 58.9358 9.202C58.9358 6.718 57.6128 5.206 55.7768 5.206C54.0218 5.206 52.6448 6.718 52.6448 9.202ZM68.3858 23.242V0.723999H71.3288V23.242H68.3858ZM49.7828 9.229C49.7828 5.206 52.2938 2.56 55.7768 2.56C58.4768 2.56 60.4748 4.045 61.3928 6.502C61.5278 6.907 61.6358 7.312 61.7168 7.744H63.5798V1.048H66.4418V22.378H63.5798V10.309H61.7978C61.7168 10.903 61.5818 11.47 61.3928 11.983C60.4748 14.386 58.5038 15.898 55.7768 15.898C52.2938 15.898 49.7828 13.252 49.7828 9.229ZM89.4707 14.98L87.5807 17.248C85.3127 15.817 83.0447 13.36 82.1807 10.903C81.3167 13.495 79.2647 16.006 76.5647 17.761L74.6207 15.547C78.1847 13.387 80.5337 9.823 80.5337 4.936V1.669H83.7737V4.801C83.7737 9.688 86.1767 12.928 89.4707 14.98ZM95.8967 23.215H92.8997V9.742H87.9317V7.042H92.8997V0.723999H95.8967V23.215ZM127.249 25.483H112.237V16.6H115.261V22.891H127.249V25.483ZM116.557 11.389V4.585H110.509V11.389H116.557ZM119.5 13.954H107.485V2.02H119.5V13.954ZM130.624 9.607H126.52V18.436H123.523V0.696999H126.52V7.042H130.624V9.607ZM146.363 12.955L146.633 15.385C142.853 16.168 138.425 16.438 132.728 16.438V2.047H135.752V13.9C139.613 13.981 143.123 13.576 146.363 12.955ZM155.462 10.633H151.358V23.215H148.334V0.723999H151.358V8.068H155.462V10.633ZM179.382 15.277H156.648V12.739H179.382V15.277ZM160.482 0.939999H163.506V7.447H175.926V10.039H160.482V0.939999ZM160.347 17.383H163.317V22.999H175.899V25.537H160.347V17.383ZM15.686 45.042C15.686 42.99 13.877 41.532 11.42 41.532C8.882 41.532 7.154 42.99 7.154 45.042C7.154 47.04 8.882 48.633 11.42 48.633C13.877 48.633 15.686 47.04 15.686 45.042ZM12.905 63.213H9.962V55.113H0.053V52.575H22.787V55.113H12.905V63.213ZM18.764 45.042C18.764 49.038 15.173 51.198 11.42 51.198C7.613 51.198 4.076 49.038 4.076 45.042C4.076 41.235 7.424 38.859 11.42 38.859C15.524 38.859 18.764 41.262 18.764 45.042ZM40.6589 52.035L40.9829 54.546C37.3649 55.383 30.9389 55.545 26.4569 55.329V46.149H34.8539V42.558H26.4029V39.966H37.8509V48.687H29.4539V52.818C32.3159 52.953 37.5539 52.683 40.6589 52.035ZM42.9269 61.215V38.724H45.8969V61.215H42.9269ZM68.0348 61.674V58.812H59.3948V61.674H68.0348ZM68.0348 52.17V38.67H71.0318V52.17H68.0348ZM56.2628 42.342H51.1598V39.777H64.2818V42.342H59.3948C59.4218 44.097 59.9888 45.582 61.0418 46.743C62.1488 47.931 63.6338 48.822 65.4968 49.416L64.0388 51.846C61.4198 50.982 59.0438 49.173 57.8558 46.824C56.8838 49.227 54.4808 51.171 51.7808 52.251L50.3768 49.794C52.1858 49.146 53.6168 48.201 54.6698 46.932C55.7228 45.69 56.2358 44.151 56.2628 42.342ZM71.0318 64.239H56.3978V53.52H59.3948V56.301H68.0348V53.52H71.0318V64.239ZM93.5821 41.343H85.8601V38.778H93.5821V41.343ZM100.089 59.298C100.089 57.57 98.5771 56.49 96.3361 56.49C94.1761 56.49 92.6371 57.57 92.6371 59.298C92.6371 61.026 94.1761 62.106 96.3361 62.106C98.5771 62.106 100.089 61.026 100.089 59.298ZM103.437 53.628H100.44V48.93H96.1741V46.419H100.44V38.643H103.437V53.628ZM88.1011 45.447H83.0521V42.909H96.3631V45.447H91.4761C91.5301 46.014 91.6651 46.554 91.8541 47.013C92.6641 48.93 94.6621 50.199 97.0651 50.847L95.7691 53.277C93.3931 52.548 90.6661 50.793 89.8021 48.876C88.9651 50.82 86.4271 52.98 83.7541 53.763L82.3501 51.387C84.6721 50.496 86.7511 49.254 87.6691 47.175C87.9121 46.662 88.0471 46.095 88.1011 45.447ZM103.113 59.298C103.113 62.457 100.305 64.671 96.3361 64.671C92.4481 64.671 89.6131 62.457 89.6131 59.298C89.6131 56.193 92.4481 53.952 96.3361 53.952C100.305 53.952 103.113 56.193 103.113 59.298ZM129.706 58.812H106.972V56.22H116.881V50.982H119.824V56.22H129.706V58.812ZM116.611 39.345H120.067V40.83C120.067 45.177 123.82 48.12 128.032 49.281L126.655 51.873C122.686 50.685 119.743 48.39 118.339 45.474C117.07 48.417 113.83 50.658 110.05 51.9L108.646 49.308C112.912 48.228 116.611 45.15 116.611 40.83V39.345Z"
      fill="black"
      fill-opacity="0.85"
    />
  </svg>
);
